import { useQueryClient } from '@tanstack/react-query';
import { useMatch, useNavigate } from '@tanstack/react-router';

import { bootstrapQueryKey, useBootstrap } from '@/features/auth/api/use-bootstrap';
import { useAuth } from '@/features/auth/use-auth';
import { useSkipPlacement, useSubmitPlacement } from '@/features/placement/api/use-placement';
import { PlacementDialog } from '@/features/placement/components/placement-dialog';
import { useStudio } from '@/shared/program/use-studio';

import type { LearnerBootstrap, LearnerProfile } from '@mio/shared';

/**
 * Shows the sign-up questionnaire once, to a learner whose onboarding is not yet resolved.
 * Saving or deciding later stamps the profile on the server; the cached bootstrap is patched
 * so the dialog never reappears in this session.
 */
export function PlacementGate() {
  const auth = useAuth();
  const learnerId = auth.user?.id ?? null;
  const bootstrap = useBootstrap(learnerId);
  const studio = useStudio();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const onPathPage = Boolean(useMatch({ from: '/app/studio/path', shouldThrow: false }));
  const submit = useSubmitPlacement();
  const skip = useSkipPlacement();
  const open = Boolean(
    bootstrap.data?.learner.onboardingCompletedAt === null && studio.data && !onPathPage,
  );
  const patch = (learner: LearnerProfile) =>
    queryClient.setQueryData<LearnerBootstrap>(bootstrapQueryKey(learnerId), (current) =>
      current ? { ...current, learner } : current,
    );
  if (!open || !studio.data) return null;
  return (
    <PlacementDialog
      open={open}
      modules={studio.data.modules.map((module) => ({
        number: module.number,
        title: module.title,
        placement: module.placement,
      }))}
      saving={submit.isPending}
      skipping={skip.isPending}
      error={submit.error?.message ?? skip.error?.message ?? null}
      onSave={(input) =>
        submit.mutate(input, {
          onSuccess: (result) => {
            patch(result.learner);
            void navigate({
              to: '/studio/modules/$number',
              params: { number: String(result.placement.path.startModule) },
            });
          },
        })
      }
      onSkip={() => skip.mutate(undefined, { onSuccess: (result) => patch(result.learner) })}
    />
  );
}
